// src/services/superAdminService.js
import {
  collection, getDocs, query, where, orderBy, limit,
  getCountFromServer, Timestamp
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Obtener conteo de usuarios por workspace
 */
export const getUsersByWorkspace = async () => {
  const snap = await getDocs(collection(db, 'users'));
  const users = snap.docs.map(d => ({ id: d.id, ...d.data() }));

  const byWorkspace = {};
  users.forEach(u => {
    const wsId = u.workspaceId || u.id;
    if (!byWorkspace[wsId]) byWorkspace[wsId] = { total: 0, active: 0, pending: 0, rejected: 0 };
    byWorkspace[wsId].total++;
    if (u.status === 'active') byWorkspace[wsId].active++;
    else if (u.status === 'pending') byWorkspace[wsId].pending++;
    else if (u.status === 'rejected') byWorkspace[wsId].rejected++;
  });

  return { users, byWorkspace, totalUsers: users.length };
};

/**
 * Obtener metricas globales del sistema
 */
export const getSystemMetrics = async () => {
  const collections = [
    { name: 'courses', label: 'Cursos' },
    { name: 'instructors', label: 'Instructores' },
    { name: 'schedules', label: 'Clases programadas' },
    { name: 'attendance_records', label: 'Registros asistencia' },
    { name: 'overtime_records', label: 'Registros horas extra' },
    { name: 'mejoras', label: 'Propuestas mejora' },
    { name: 'direct_chats', label: 'Chats directos' },
    { name: 'group_chats', label: 'Chats grupales' },
  ];

  const metrics = {};
  await Promise.all(
    collections.map(async (col) => {
      try {
        const snap = await getCountFromServer(collection(db, col.name));
        metrics[col.name] = { label: col.label, count: snap.data().count };
      } catch {
        metrics[col.name] = { label: col.label, count: 0 };
      }
    })
  );

  return metrics;
};

/**
 * Obtener registros recientes (actividad global)
 */
export const getRecentActivity = async (limitCount = 20) => {
  const activity = [];

  // Usuarios recientes
  try {
    const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'), limit(limitCount));
    const snap = await getDocs(q);
    snap.docs.forEach(d => {
      const data = d.data();
      activity.push({
        type: 'user_registered',
        label: `${data.displayName || data.email} se registro`,
        email: data.email,
        date: data.createdAt,
        status: data.status,
      });
    });
  } catch { /* ignore */ }

  // Workspaces recientes
  try {
    const q = query(collection(db, 'workspaces'), orderBy('createdAt', 'desc'), limit(limitCount));
    const snap = await getDocs(q);
    snap.docs.forEach(d => {
      const data = d.data();
      activity.push({
        type: 'workspace_created',
        label: `Empresa "${data.name}" registrada`,
        email: data.ownerEmail,
        date: data.createdAt,
        status: data.status,
      });
    });
  } catch { /* ignore */ }

  // Ordenar por fecha descendente
  activity.sort((a, b) => {
    const dateA = a.date?.toDate ? a.date.toDate() : new Date(0);
    const dateB = b.date?.toDate ? b.date.toDate() : new Date(0);
    return dateB - dateA;
  });

  return activity.slice(0, limitCount);
};

/**
 * Obtener crecimiento de usuarios por mes (ultimos 6 meses)
 */
export const getUserGrowth = async () => {
  const snap = await getDocs(collection(db, 'users'));
  const users = snap.docs.map(d => d.data());

  const months = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      month: d.toLocaleDateString('es', { month: 'short', year: '2-digit' }),
      start: d,
      end: new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59),
      users: 0,
      workspaces: 0,
    });
  }

  users.forEach(u => {
    const created = u.createdAt?.toDate ? u.createdAt.toDate() : null;
    if (!created) return;
    months.forEach(m => {
      if (created >= m.start && created <= m.end) m.users++;
    });
  });

  // Workspaces growth
  try {
    const wsSnap = await getDocs(collection(db, 'workspaces'));
    wsSnap.docs.forEach(d => {
      const data = d.data();
      const created = data.createdAt?.toDate ? data.createdAt.toDate() : null;
      if (!created) return;
      months.forEach(m => {
        if (created >= m.start && created <= m.end) m.workspaces++;
      });
    });
  } catch { /* ignore */ }

  return months.map(m => ({ month: m.month, users: m.users, workspaces: m.workspaces }));
};
